/**
 * Composant PlayerHand - Affiche une main de cartes en éventail
 */

import { memo, useMemo, useState, useCallback, useEffect, useRef } from 'react';
import Card from './Card';
import type { CardNumber } from '../types/game';
import type { HandLayout } from '../types/app';
import { sortHand } from '../utils/cards';
import * as haptics from '../utils/haptics';
import { useTableLayout } from '../hooks/useTableLayout';
import { computeArcGeometry } from '../utils/tableLayout';
import '../styles/player-hand.css';

// === Constantes pour le drag-to-play tactile ===
/** Distance verticale (px) à atteindre pour "armer" la carte avant de la jouer. */
const DRAG_ARM_THRESHOLD_Y = -60;
/** Durée max (ms) entre pointerdown et pointerup pour considérer un tap simple. */
const TAP_MAX_DURATION_MS = 250;
/** Distance max (px) du déplacement pour considérer un tap simple. */
const TAP_MAX_DISTANCE = 8;

interface DragState {
  pointerId: number;
  pointerType: string;
  cardIndex: number;
  card: CardNumber;
  startX: number;
  startY: number;
  startTime: number;
  currentY: number;
  currentX: number;
  armed: boolean;
  /** true une fois le geste devenu vertical : on ne change plus de carte. */
  lockedVertical: boolean;
}

interface PlayerHandProps {
  /** Cartes dans la main */
  cards: CardNumber[];
  
  /** Cartes jouables (pour surlignage) */
  playableCards?: CardNumber[];
  
  /** Carte(s) sélectionnée(s) */
  selectedCards?: CardNumber[];
  
  /** Handler de clic sur une carte */
  onCardClick?: (card: CardNumber) => void;
  
  /** Handler de sélection multiple */
  onSelectionChange?: (cards: CardNumber[]) => void;
  
  /** Mode de sélection multiple (pour l'écart) */
  multiSelect?: boolean;
  
  /** Nombre max de cartes sélectionnables */
  maxSelection?: number;
  
  /** Taille des cartes */
  cardSize?: 'small' | 'medium' | 'large';
  
  /** Trier automatiquement la main */
  autoSort?: boolean;
  
  /** Animation de distribution */
  animate?: boolean;
  
  /** Bloque la validation (jouer / sélectionner), pas le scrub. */
  disabled?: boolean;
  
  /** Position (pour orientation) */
  position?: 'bottom' | 'left' | 'right' | 'top';
  
  /** Disposition : alignée (flat) ou en arc elliptique (arc) */
  handLayout?: HandLayout;
  
  /** Classe CSS additionnelle */
  className?: string;
}

/**
 * Calcule les paramètres d'éventail selon le nombre de cartes
 */
/**
 * Fenêtre étroite autour de la carte survolée : les 1–2 voisines s'écartent
 * un peu, les suivantes se resserrent vers elle pour ne pas ouvrir le tapis.
 */
function arcHoverRotation(
  baseRot: number,
  index: number,
  hovered: number | null,
  n: number,
): number {
  if (hovered === null || n < 3) return baseRot;
  const dist = index - hovered;
  if (dist === 0) return baseRot;
  const abs = Math.abs(dist);
  const sign = Math.sign(dist);
  const coef = n < 12 ? Math.max(0.4, n / 12) : 1;
  if (abs === 1) return baseRot + sign * 1.35 * coef;
  if (abs === 2) return baseRot + sign * 0.55 * coef;
  const compress = 0.22 * Math.max(0, 1 - (abs - 2) / 7) * coef;
  return baseRot - sign * compress;
}

function calculateFanParams(
  cardCount: number,
  position: 'bottom' | 'left' | 'right' | 'top'
) {
  // Angles max selon la position
  const maxAngle = position === 'bottom' || position === 'top' ? 30 : 20;
  
  // Plus on a de cartes, plus l'angle total est grand (mais plafonné)
  const totalAngle = Math.min(maxAngle, cardCount * 2);
  
  // Angle de départ (moitié de l'angle total vers la gauche)
  const startAngle = -totalAngle / 2;
  
  // Incrément par carte
  const angleStep = cardCount > 1 ? totalAngle / (cardCount - 1) : 0;
  
  // Décalage vertical pour l'effet d'arc
  const maxOffset = Math.min(20, cardCount * 1.5);
  
  return { startAngle, angleStep, maxOffset, totalAngle };
}


const PlayerHand = memo(function PlayerHand({
  cards,
  playableCards,
  selectedCards = [],
  onCardClick,
  onSelectionChange,
  multiSelect = false,
  maxSelection = 6,
  cardSize = 'medium',
  autoSort = true,
  animate = false,
  disabled = false,
  position = 'bottom',
  handLayout = 'flat',
  className = '',
}: PlayerHandProps) {
  const useArc = handLayout === 'arc' && position === 'bottom';
  // État local pour la sélection multiple
  const [localSelection, setLocalSelection] = useState<CardNumber[]>(selectedCards);

  // Synchroniser localSelection quand selectedCards change de l'extérieur
  // (ex: clic sur une carte dans le panneau d'écart pour la retirer)
  useEffect(() => {
    if (multiSelect) {
      setLocalSelection(prev => {
        if (prev.length === selectedCards.length && prev.every((c, i) => c === selectedCards[i])) {
          return prev;
        }
        return [...selectedCards];
      });
    }
  }, [selectedCards, multiSelect]);
  
  // Survol par identité de carte (pas par index : après un coup l'index
  // pointerait sinon la voisine).
  const [hoveredCard, setHoveredCard] = useState<CardNumber | null>(null);
  
  const containerRef = useRef<HTMLDivElement>(null);

  // Cartes triées
  const sortedCards = useMemo(() => {
    return autoSort ? sortHand(cards) : cards;
  }, [cards, autoSort]);
  
  // Set des cartes jouables pour lookup rapide
  const playableSet = useMemo(() => {
    return new Set(playableCards || cards);
  }, [playableCards, cards]);

  const playableSignature = useMemo(
    () => [...playableSet].sort((a, b) => a - b).join(','),
    [playableSet],
  );
  
  // Set des cartes sélectionnées
  const selectionSet = useMemo(() => {
    return new Set(multiSelect ? localSelection : selectedCards);
  }, [multiSelect, localSelection, selectedCards]);

  // L'écart garde les cartes dans l'éventail (highlight), pas de disparition.
  const visibleCards = sortedCards;

  const hoveredCardIndex = useMemo(() => {
    if (hoveredCard === null) return null;
    const i = visibleCards.indexOf(hoveredCard);
    return i >= 0 ? i : null;
  }, [hoveredCard, visibleCards]);

  const { layout, isResizing } = useTableLayout({
    handCount: cards.length,
    handLayout: useArc ? 'arc' : 'flat',
  });
  const containerWidth = layout.handAvailableWidth;
  const baseCardWidth = layout.cardWidth;
  const baseCardHeight = layout.cardHeight;

  const arcGeometry = useMemo(() => {
    if (!useArc) return null;
    return computeArcGeometry(
      visibleCards.length,
      layout.handAvailableWidth,
      layout.containerHeight,
      layout.cardWidth,
      layout.cardHeight,
      undefined,
      layout.portrait,
    );
  }, [useArc, visibleCards.length, layout.handAvailableWidth, layout.containerHeight, layout.cardWidth, layout.cardHeight, layout.portrait]);
  
  // Handler de clic
  const handleCardClick = useCallback((card: CardNumber) => {
    if (disabled) return;
    if (!playableSet.has(card)) return;
    
    if (multiSelect && onSelectionChange) {
      // Mode sélection multiple
      let newSelection: CardNumber[];
      
      if (localSelection.includes(card)) {
        // Désélectionner
        newSelection = localSelection.filter(c => c !== card);
        haptics.tap();
      } else if (localSelection.length < maxSelection) {
        // Sélectionner (si pas au max)
        newSelection = [...localSelection, card];
        haptics.select();
      } else {
        // Max atteint, ne rien faire
        return;
      }
      
      setLocalSelection(newSelection);
      onSelectionChange(newSelection);
    } else if (onCardClick) {
      // Mode clic simple
      onCardClick(card);
    }
  }, [disabled, playableSet, multiSelect, localSelection, maxSelection, onSelectionChange, onCardClick]);

  const canLiftCard = useCallback((card: CardNumber) => {
    if (disabled) return false;
    if (!playableSet.has(card)) return false;
    if (!multiSelect) return true;
    // Fantôme : retrait par tap uniquement, pas de lift / scrub.
    if (localSelection.includes(card)) return false;
    return localSelection.length < maxSelection;
  }, [disabled, playableSet, multiSelect, localSelection, maxSelection]);

  const isEcartGhost = useCallback((card: CardNumber) => {
    return multiSelect && selectionSet.has(card);
  }, [multiSelect, selectionSet]);
  
  // Survol souris uniquement. Sur tactile, un tap synthétise mouseenter et
  // laisserait la carte levée ; l'inspection passe par le scrub.
  const handleCardHover = useCallback((card: CardNumber) => {
    if (typeof window !== 'undefined'
      && !window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      return;
    }
    if (isEcartGhost(card)) return;
    setHoveredCard(card);
  }, [isEcartGhost]);
  
  const handleCardLeave = useCallback(() => {
    setHoveredCard(null);
  }, []);

  useEffect(() => {
    if (hoveredCard !== null && isEcartGhost(hoveredCard)) {
      setHoveredCard(null);
    }
  }, [hoveredCard, isEcartGhost]);

  // === État de drag-to-play (touch / stylus) ===
  const dragStateRef = useRef<DragState | null>(null);
  const [dragVisual, setDragVisual] = useState<{ index: number; dx: number; dy: number; armed: boolean } | null>(null);
  // Flag pour étouffer le click synthétique qui suit un pointerup tactile
  // (sinon, la carte serait jouée 2 fois : 1 fois via pointerup, 1 fois via onClick).
  const suppressNextClickRef = useRef(false);

  // Refs pour la géométrie de layout (mises à jour à chaque render).
  // Évite les problèmes de TDZ avec useCallback : les handlers
  // lisent toujours la valeur courante via la ref.
  const layoutRef = useRef({
    n: 0,
    startOffset: 0,
    effectiveCardWidth: 0,
    visibleRatio: 0,
    useArc: false,
    visibleCards: [] as CardNumber[],
    arc: null as null | { pivotX: number; pivotY: number; spacingDeg: number; startAngleDeg: number },
  });
  
  // Classes CSS
  const handClasses = [
    'player-hand',
    `player-hand-${position}`,
    `player-hand-${cardSize}`,
    useArc && 'player-hand-arc',
    disabled && 'player-hand-disabled',
    hoveredCardIndex !== null && 'player-hand-hovering',
    dragVisual && 'player-hand-carrying',
    className,
  ].filter(Boolean).join(' ');
  
  const availableWidth = layout.handAvailableWidth;
  
  const n = visibleCards.length;
  
  // Calculer la largeur totale nécessaire avec chevauchement
  // On veut que les cartes se chevauchent suffisamment
  const minVisibleRatio = 0.18; // Au minimum 18% de la carte visible (très serré)
  const maxVisibleRatio = 0.45; // Au maximum 45% de la carte visible
  
  // Calculer le ratio nécessaire pour tenir dans l'espace
  // Formule : availableWidth = cardWidth + (n-1) * cardWidth * visibleRatio
  // Donc : visibleRatio = (availableWidth - cardWidth) / ((n-1) * cardWidth)
  
  let visibleRatio = maxVisibleRatio;
  let scaleFactor = 1;
  
  if (n > 1) {
    const neededRatio = (availableWidth - baseCardWidth) / ((n - 1) * baseCardWidth);
    
    if (neededRatio >= maxVisibleRatio) {
      // Assez de place, utiliser le ratio max
      visibleRatio = maxVisibleRatio;
    } else if (neededRatio >= minVisibleRatio) {
      // On peut tenir avec un ratio intermédiaire
      visibleRatio = neededRatio;
    } else {
      // Même avec le ratio min, ça ne tient pas -> réduire la taille des cartes
      // On veut : availableWidth = scaledWidth + (n-1) * scaledWidth * minVisibleRatio
      // availableWidth = scaledWidth * (1 + (n-1) * minVisibleRatio)
      // scaledWidth = availableWidth / (1 + (n-1) * minVisibleRatio)
      const neededWidth = availableWidth / (1 + (n - 1) * minVisibleRatio);
      scaleFactor = Math.max(0.5, neededWidth / baseCardWidth); // Min 50%
      visibleRatio = minVisibleRatio;
    }
  }
  
  // Largeur effective de la carte (visuelle et pour le calcul du margin)
  const effectiveCardWidth = baseCardWidth * scaleFactor;
  
  // Margin = partie de la carte cachée par la carte suivante (valeur négative)
  // IMPORTANT: utiliser la largeur effective pour que le layout soit cohérent
  // Le margin doit toujours être négatif (ou zéro) pour que les cartes se chevauchent correctement
  const _baseMargin = n > 1 ? Math.min(0, -effectiveCardWidth * (1 - visibleRatio)) : 0;
  void _baseMargin; // Prévu pour usage futur
  
  // === EFFET DE SERRAGE (inspiré du code legacy gui.js zoom_main) ===
  // Quand une carte est survolée, on ajoute de l'espace APRÈS cette carte
  // MAIS on compense en réduisant l'espacement des autres cartes pour garder la même largeur totale
  
  // Coefficient selon le nombre de cartes (moins de cartes = moins d'effet)
  let hoverCoef = 1;
  if (n < 15) {
    const deduc = 15 - (15 - n) * 2;
    hoverCoef = Math.max(0, deduc / 15);
  }
  
  // Espace supplémentaire à ajouter après la carte survolée (proportionnel à la taille)
  const hoverExtraSpace = effectiveCardWidth * 0.20 * hoverCoef; // 20% de la largeur de carte
  
  // Calculer la largeur totale occupée par les cartes (constante, avec ou sans survol)
  const totalCardsWidth = n > 0 ? effectiveCardWidth + (n - 1) * effectiveCardWidth * visibleRatio : 0;
  
  // Calculer le décalage pour centrer les cartes
  // Ne jamais avoir un offset négatif (sinon les cartes sortent du conteneur et sont coupées)
  const startOffset = Math.max(0, (containerWidth - totalCardsWidth) / 2);
  
  // Au survol, calculer le ratio réduit pour compenser l'espace ajouté après la carte survolée
  // On veut que la largeur totale reste la même
  // SAUF si c'est la dernière carte : pas besoin de faire de place car aucune carte ne la recouvre
  const isLastCardHovered = hoveredCardIndex === n - 1;
  const hoverAdjustedRatio = hoveredCardIndex !== null && n > 1 && !isLastCardHovered
    ? visibleRatio - hoverExtraSpace / ((n - 1) * effectiveCardWidth)
    : visibleRatio;

  // === Mise à jour de la ref de layout pour les handlers tactiles ===
  // (lus à chaque pointermove, donc toujours à jour avec le dernier render)
  layoutRef.current = {
    n,
    startOffset,
    effectiveCardWidth,
    visibleRatio,
    useArc,
    visibleCards,
    arc: arcGeometry
      ? {
          pivotX: arcGeometry.pivotX,
          pivotY: arcGeometry.pivotY,
          spacingDeg: arcGeometry.spacingDeg,
          startAngleDeg: arcGeometry.startAngleDeg,
        }
      : null,
  };

  // === Handlers tactiles (Pointer Events) ===
  // Définis ici car ils dépendent indirectement (via layoutRef) de la géométrie
  // calculée plus haut. Les useCallback gardent une identité stable grâce
  // à l'usage de layoutRef.

  const findCardIndexAtPoint = useCallback((containerX: number, containerY: number): number | null => {
    const { n: ln, useArc: la, startOffset: so, effectiveCardWidth: ew, visibleRatio: vr, arc } = layoutRef.current;
    if (ln === 0) return null;

    if (la && arc) {
      const dx = containerX - arc.pivotX;
      const dy = containerY - arc.pivotY;
      const angleDeg = (Math.atan2(dx, -dy) * 180) / Math.PI;
      let best = 0;
      let bestDist = Infinity;
      for (let i = 0; i < ln; i++) {
        const rot = arc.startAngleDeg + i * arc.spacingDeg;
        const dist = Math.abs(angleDeg - rot);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      }
      const tolerance = Math.max(arc.spacingDeg * 0.9, 5);
      return bestDist <= tolerance ? best : null;
    }

    if (containerX < 0) return null;
    let idx = -1;
    for (let i = 0; i < ln; i++) {
      const left = so + i * ew * vr;
      const right = left + ew;
      if (containerX >= left && containerX <= right) {
        idx = i;
      }
    }
    return idx >= 0 ? idx : null;
  }, []);

  const isOverPlaymat = useCallback((clientX: number, clientY: number): boolean => {
    const trick = document.querySelector('.trick-display');
    if (!trick) return false;
    const rect = trick.getBoundingClientRect();
    return clientX >= rect.left && clientX <= rect.right
      && clientY >= rect.top && clientY <= rect.bottom;
  }, []);

  const resetDrag = useCallback(() => {
    dragStateRef.current = null;
    setDragVisual(null);
    setHoveredCard(null);
  }, []);

  // Le jeu peut avancer pendant un doigt encore posé (pli qui se complète,
  // tour qui arrive, animation qui se termine). On ré-évalue le geste
  // sur les règles courantes, sans attendre le prochain pointermove.
  useEffect(() => {
    const drag = dragStateRef.current;
    if (!drag) return;

    const idx = visibleCards.indexOf(drag.card);
    if (idx < 0) {
      resetDrag();
      return;
    }
    drag.cardIndex = idx;

    const canCommit = canLiftCard(drag.card);
    const dy = drag.currentY - drag.startY;
    const lifting = dy < -12;

    if ((drag.lockedVertical || drag.armed) && !canCommit) {
      drag.lockedVertical = false;
      drag.armed = false;
      setDragVisual(null);
      setHoveredCard(drag.card);
      haptics.tap();
      return;
    }

    if (!drag.lockedVertical && canCommit && lifting) {
      drag.lockedVertical = true;
      // Le scrub a pu déplacer le doigt loin du pointerdown : on ne
      // reprend que la course verticale, pas le trajet le long de l'arc.
      drag.startX = drag.currentX;
      setHoveredCard(null);
      const overPlaymat = isOverPlaymat(drag.currentX, drag.currentY);
      const armed = dy < DRAG_ARM_THRESHOLD_Y || overPlaymat;
      if (armed && !drag.armed) {
        haptics.arm();
      }
      drag.armed = armed;
      setDragVisual({
        index: idx,
        dx: 0,
        dy: Math.min(0, dy),
        armed,
      });
    }
  }, [playableSignature, canLiftCard, visibleCards, isOverPlaymat, resetDrag]);

  const handleContainerPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (dragStateRef.current) return;
    if (e.pointerType === 'mouse') return;

    const container = containerRef.current;
    if (!container) return;
    const cardsEl = container.querySelector('.hand-cards') ?? container;
    const rect = cardsEl.getBoundingClientRect();
    const idx = findCardIndexAtPoint(e.clientX - rect.left, e.clientY - rect.top);
    if (idx !== null && idx !== hoveredCardIndex) {
      const next = layoutRef.current.visibleCards[idx];
      if (next !== undefined && !isEcartGhost(next)) {
        setHoveredCard(next);
        haptics.tap();
      }
    }
  }, [findCardIndexAtPoint, hoveredCardIndex, isEcartGhost]);

  const handleContainerPointerLeave = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (dragStateRef.current) return;
    if (e.pointerType === 'mouse') return;
    setHoveredCard(null);
  }, []);

  const handleCardPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>, index: number, card: CardNumber) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;

    // Capture aussi en écart : sinon le scrub ne reçoit plus les pointermove.
    // Pas de hover ici : un tap ne doit rien lever (raccourci play seulement).
    if (e.pointerType !== 'mouse') {
      try {
        (e.currentTarget as Element).setPointerCapture(e.pointerId);
      } catch {
        // ignore
      }
    }

    dragStateRef.current = {
      pointerId: e.pointerId,
      pointerType: e.pointerType,
      cardIndex: index,
      card,
      startX: e.clientX,
      startY: e.clientY,
      startTime: performance.now(),
      currentX: e.clientX,
      currentY: e.clientY,
      armed: false,
      lockedVertical: false,
    };
  }, []);

  const handleCardPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragStateRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    if (drag.pointerType === 'mouse') return;

    drag.currentX = e.clientX;
    drag.currentY = e.clientY;
    let dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;

    const lifting = dy < -12;
    const canPlayDrag = canLiftCard(drag.card);
    const overPlaymat = isOverPlaymat(e.clientX, e.clientY);
    const backInHand = dy > -10 && !overPlaymat;

    if (drag.lockedVertical && backInHand) {
      drag.lockedVertical = false;
      drag.armed = false;
      setDragVisual(null);
    }

    if (!drag.lockedVertical) {
      if (lifting && canPlayDrag) {
        // Tout mouvement vers le tapis (y compris diagonal depuis un bord
        // de l'arc) verrouille la carte : on ne re-hit-teste plus dessous.
        drag.lockedVertical = true;
        // Origine X = position actuelle, sinon le trajet de scrub se
        // retrouve additionné et la carte saute vers le bord.
        drag.startX = e.clientX;
        dx = 0;
        setHoveredCard(null);
      } else {
        const container = containerRef.current;
        const cardsEl = container?.querySelector('.hand-cards') ?? container;
        if (cardsEl) {
          const rect = cardsEl.getBoundingClientRect();
          const idx = findCardIndexAtPoint(e.clientX - rect.left, e.clientY - rect.top);
          if (idx !== null) {
            const next = layoutRef.current.visibleCards[idx];
            if (next !== undefined && !isEcartGhost(next)) {
              if (idx !== drag.cardIndex) {
                drag.cardIndex = idx;
                drag.card = next;
                haptics.tap();
              }
              setHoveredCard(next);
            }
          }
        }
        setDragVisual(null);
        drag.armed = false;
        return;
      }
    }

    if (!canPlayDrag) return;

    const verticalArmed = dy < DRAG_ARM_THRESHOLD_Y;
    const armed = verticalArmed || overPlaymat || (drag.armed && !backInHand);

    if (armed && !drag.armed) {
      haptics.arm();
    }
    drag.armed = armed;

    setDragVisual({ index: drag.cardIndex, dx, dy: Math.min(0, dy), armed });
  }, [canLiftCard, isOverPlaymat, findCardIndexAtPoint, isEcartGhost]);

  const handleCardPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragStateRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;

    const elapsed = performance.now() - drag.startTime;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    const distance = Math.hypot(dx, dy);

    try {
      (e.currentTarget as Element).releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }

    if (drag.pointerType !== 'mouse' && drag.armed) {
      if (canLiftCard(drag.card)) {
        if (!multiSelect) haptics.success();
        suppressNextClickRef.current = true;
        handleCardClick(drag.card);
      } else {
        haptics.tap();
      }
      resetDrag();
      return;
    }

    if (drag.pointerType !== 'mouse'
      && elapsed <= TAP_MAX_DURATION_MS
      && distance <= TAP_MAX_DISTANCE) {
      suppressNextClickRef.current = true;
      handleCardClick(drag.card);
      resetDrag();
      return;
    }

    resetDrag();
  }, [canLiftCard, multiSelect, handleCardClick, resetDrag]);

  /**
   * onClickCapture sur le wrapper : étouffe le click synthétique généré
   * par le navigateur après un pointerup tactile que nous avons déjà traité.
   */
  const handleWrapperClickCapture = useCallback((e: React.MouseEvent) => {
    if (suppressNextClickRef.current) {
      suppressNextClickRef.current = false;
      e.stopPropagation();
      e.preventDefault();
    }
  }, []);

  const handleCardPointerCancel = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragStateRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    try {
      (e.currentTarget as Element).releasePointerCapture(e.pointerId);
    } catch {
      // ignore
    }
    resetDrag();
  }, [resetDrag]);

  // ---- Rendu des cartes ----

  const renderCard = (card: CardNumber, index: number) => {
    const isPlayable = playableSet.has(card);
    const isSelected = selectionSet.has(card);
    const isHovered = hoveredCardIndex === index;

    const isDragging = dragVisual?.index === index;
    const isArmed = isDragging && dragVisual?.armed;

    if (useArc && arcGeometry?.positions[index]) {
      const pos = arcGeometry.positions[index];
      const rot = arcHoverRotation(pos.rotation, index, hoveredCardIndex, n);

      const baseTransform = `rotate(${rot}deg)`;
      const hoverTransform = `rotate(${rot}deg) translate(0px, -14px)`;
      const dragTransform = isDragging
        ? `translate(${dragVisual!.dx}px, ${dragVisual!.dy}px) rotate(${rot}deg)`
        : null;

      const wrapperStyle: React.CSSProperties = {
        position: 'absolute',
        left: `${pos.left}px`,
        top: `${pos.top}px`,
        width: `${baseCardWidth}px`,
        zIndex: isDragging ? 1000 : index,
        transformOrigin: `${baseCardWidth / 2}px ${pos.pivotY}px`,
        transform: dragTransform ?? (isHovered ? hoverTransform : baseTransform),
        transition: isResizing || isDragging ? 'none' : 'transform 0.15s ease-out',
        touchAction: 'none',
      };

      const wrapperClasses = [
        'hand-card-wrapper',
        isHovered && 'hand-card-hovered',
        isDragging && 'hand-card-dragging',
        isArmed && 'hand-card-armed',
        multiSelect && isSelected && 'hand-card-ghost',
      ].filter(Boolean).join(' ');

      return (
        <div
          key={card}
          data-card-number={card}
          className={wrapperClasses}
          style={wrapperStyle}
          onMouseEnter={() => handleCardHover(card)}
          onMouseLeave={handleCardLeave}
          onPointerDown={(e) => handleCardPointerDown(e, index, card)}
          onPointerMove={handleCardPointerMove}
          onPointerUp={handleCardPointerUp}
          onPointerCancel={handleCardPointerCancel}
          onClickCapture={handleWrapperClickCapture}
        >
          <Card
            card={card}
            size={cardSize}
            width={baseCardWidth}
            height={baseCardHeight}
            selected={!multiSelect && isSelected}
            playable={isPlayable}
            disabled={!isPlayable}
            onClick={handleCardClick}
            rotation={0}
            offsetY={0}
            zIndex={index}
            animate={animate}
            animationDelay={animate ? index * 50 : 0}
            className="hand-card"
          />
        </div>
      );
    }

    // === MODE FLAT ===
    const angle = 0;
    const offsetY = 0;

    let cardLeft = startOffset + index * effectiveCardWidth * hoverAdjustedRatio;
    if (hoveredCardIndex !== null && index > hoveredCardIndex && !isLastCardHovered) {
      cardLeft += hoverExtraSpace;
    }

    let transformValue: string | undefined;
    if (isDragging) {
      const { dx, dy } = dragVisual!;
      const armScale = isArmed ? 1.08 : 1.04;
      if (scaleFactor < 1) {
        transformValue = `scale(${scaleFactor * armScale}) translate(${dx / scaleFactor}px, ${dy / scaleFactor}px)`;
      } else {
        transformValue = `translate(${dx}px, ${dy}px) scale(${armScale})`;
      }
    } else if (scaleFactor < 1 && isHovered) {
      transformValue = `scale(${scaleFactor}) translateY(-12px)`;
    } else if (scaleFactor < 1) {
      transformValue = `scale(${scaleFactor})`;
    } else if (isHovered) {
      transformValue = 'translateY(-12px)';
    }

    const wrapperStyle: React.CSSProperties = {
      position: 'absolute',
      left: `${cardLeft}px`,
      bottom: 0,
      transform: transformValue,
      transformOrigin: 'center bottom',
      width: `${effectiveCardWidth}px`,
      zIndex: isDragging ? 1000 : index,
      transition: isResizing || isDragging ? 'none' : 'left 0.15s ease-out, transform 0.15s ease-out',
      touchAction: 'none',
    };

    const wrapperClasses = [
      'hand-card-wrapper',
      isHovered && 'hand-card-hovered',
      isDragging && 'hand-card-dragging',
      isArmed && 'hand-card-armed',
      multiSelect && isSelected && 'hand-card-ghost',
    ].filter(Boolean).join(' ');

    return (
      <div
        key={card}
        data-card-number={card}
        className={wrapperClasses}
        style={wrapperStyle}
        onMouseEnter={() => handleCardHover(card)}
        onPointerDown={(e) => handleCardPointerDown(e, index, card)}
        onPointerMove={handleCardPointerMove}
        onPointerUp={handleCardPointerUp}
        onPointerCancel={handleCardPointerCancel}
        onClickCapture={handleWrapperClickCapture}
      >
        <Card
          card={card}
          size={cardSize}
          width={effectiveCardWidth}
          height={baseCardHeight * scaleFactor}
          selected={!multiSelect && isSelected}
          playable={isPlayable}
          disabled={!isPlayable}
          onClick={handleCardClick}
          rotation={angle}
          offsetY={offsetY}
          zIndex={index}
          animate={animate}
          animationDelay={animate ? index * 50 : 0}
          className="hand-card"
        />
      </div>
    );
  };

  return (
    <div
      className={handClasses}
      onMouseLeave={handleCardLeave}
      onPointerMove={handleContainerPointerMove}
      onPointerLeave={handleContainerPointerLeave}
      ref={containerRef}
    >
      <div className="hand-cards" style={{ position: 'relative', width: '100%' }}>
        {visibleCards.map((card, index) => renderCard(card, index))}
      </div>
    </div>
  );
});

export default PlayerHand;

// ============================================
// Composant pour les mains adverses (cachées)
// ============================================

interface OpponentHandProps {
  /** Nombre de cartes */
  cardCount: number;
  
  /** Position du joueur */
  position: 'left' | 'right' | 'top';
  
  /** Pseudo du joueur */
  pseudo?: string;
  
  /** Taille des cartes */
  cardSize?: 'small' | 'medium';
  
  /** Classe CSS additionnelle */
  className?: string;
}

export function OpponentHand({
  cardCount,
  position,
  pseudo,
  cardSize = 'small',
  className = '',
}: OpponentHandProps) {
  const fanParams = calculateFanParams(cardCount, position);
  
  // Pour les positions latérales, on affiche verticalement
  const isVertical = position === 'left' || position === 'right';
  
  const handClasses = [
    'opponent-hand',
    `opponent-hand-${position}`,
    className,
  ].filter(Boolean).join(' ');
  
  return (
    <div className={handClasses}>
      {pseudo && <span className="opponent-pseudo">{pseudo}</span>}
      <div className="opponent-cards">
        {Array.from({ length: cardCount }).map((_, index) => {
          const angle = isVertical 
            ? 0 
            : fanParams.startAngle + (index * fanParams.angleStep);
          
          return (
            <Card
              key={index}
              card={null}
              size={cardSize}
              rotation={angle}
              zIndex={index}
              className="opponent-card"
            />
          );
        })}
      </div>
      <span className="opponent-card-count">{cardCount}</span>
    </div>
  );
}
